// src/features/owner/OwnerLayout.tsx — full-canvas owner chrome (BareLayout host). Mirrors SettingsLayout.
import styled from 'styled-components';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { useViewport } from '@/hooks';
import { media } from '@/theme/media';
import { OWNER_GROUPS, OWNER_SECTIONS } from '@/core/data/owner-sections';
import { SectionNavMobile } from '@/app/shell/SectionNavMobile';
import { OwnerTopbar } from './OwnerTopbar';
import { OwnerSidebar } from './OwnerSidebar';

const Page = styled.div`
  min-height:100vh; display:flex; flex-direction:column;
  background:${({ theme }) => theme.color.bgRoot};
`;
const Bar = styled.div` padding:16px 24px 0; `;
const Body = styled.div`
  display:grid; grid-template-columns:260px minmax(0,1fr); gap:24px;
  /* Full-bleed canvas: matches SettingsLayout. Was capped at 1400px + centred,
     which left ~260px of dead margin either side on a 1920px display. */
  flex:1 0 auto;
  padding:0 24px 24px;
  @media (max-width:980px) { grid-template-columns:1fr; }
`;
const Pane = styled.div` min-width:0; `;

/** Desktop sidebar — always in DOM; CSS hides on mobile. */
const SidebarWrap = styled.div`
  ${media.mobile`display: none;`}
`;

/** Mobile section-switcher — rendered by JS when mobile; CSS also hides on tablet/desktop. */
const MobileNavWrap = styled.div`
  margin-bottom: 16px;
  ${media.tabletUp`display: none;`}
`;

/**
 * Flattens Dashboard + all grouped sections into the SectionNavMobile shape.
 * Groups are indicated with a prefix label for clarity on mobile.
 */
function buildMobileSections() {
  const dashboard = [{ id: 'dashboard', label: 'Dashboard', icon: 'home' as const }];
  const grouped = OWNER_GROUPS.flatMap((group) =>
    OWNER_SECTIONS.filter((s) => s.group === group.key).map((s) => ({
      id: s.id,
      label: s.label,
      icon: s.icon,
    })),
  );
  return [...dashboard, ...grouped];
}

export interface OwnerLayoutProps { activeId: string; title: string; actions?: ReactNode; children: ReactNode; }

export function OwnerLayout({ activeId, title, actions, children }: OwnerLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const viewport = useViewport();

  const mobileSections = buildMobileSections();

  function handleBack() {
    if (location.key === 'default') navigate('/');
    else navigate(-1);
  }

  function handleSectionSelect(id: string) {
    if (id === 'dashboard') {
      navigate('/owner', { replace: true });
    } else {
      const section = OWNER_SECTIONS.find((s) => s.id === id);
      if (section) navigate(section.path, { replace: true });
    }
  }

  return (
    <Page>
      <Bar>
        <Button variant="ghost" onClick={handleBack}>
          <Icon name="arrow-left" size={16} /> Back
        </Button>
      </Bar>
      <OwnerTopbar title={title} actions={actions} />
      <Body>
        {/* Sidebar: always in DOM; CSS hides on mobile */}
        <SidebarWrap data-testid="owner-sidebar-wrap">
          <OwnerSidebar activeId={activeId} />
        </SidebarWrap>
        <Pane>
          {/* Mobile nav: JS-gated for mobile; CSS also hides on tablet/desktop */}
          {viewport === 'mobile' && (
            <MobileNavWrap data-testid="owner-mobile-nav-wrap">
              <SectionNavMobile
                sections={mobileSections}
                activeId={activeId}
                onSelect={handleSectionSelect}
              />
            </MobileNavWrap>
          )}
          {children}
        </Pane>
      </Body>
    </Page>
  );
}
